import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WordWormGameModal from './WordWorm';
import { IMAGES } from 'assets/images';
import ItemCard from '../common/ItemCard';
import useApiCall from 'contexts/ApiCall';
import gameService, { Game } from 'services/gameService';
import { useUserContext } from 'contexts/UserContext';
import TilePileGameModal from './TilePile';
import Modal from '../common/Modal';
import CachingEngine from 'utils/CachingEngine';

interface GameMenuProps {
  isOpen: boolean;
  onClose: () => void;
  reopen: () => void;
}

type GameName = 'Tile Pile' | 'Word Worm';

// Cookie utility functions
const getLocalHighScore = (gameId: number): number => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`high_score_${gameId}=`));
  return cookie ? parseInt(cookie.split('=')[1], 10) : 0;
};

const setLocalHighScore = (gameId: number, score: number) => {
  document.cookie = `high_score_${gameId}=${score.toString()}; path=/; max-age=31536000`; // 1 year expiry
};

const GameMenu: React.FC<GameMenuProps> = ({ isOpen, onClose, reopen }) => {
  const { user } = useUserContext();
  const [isWordWormOpen, setIsWordWormOpen] = useState<boolean>(false);
  const [isTilePileOpen, setIsTilePileOpen] = useState<boolean>(false);
  const [games, setGames] = useState<Game[]>([]);
  const [currentGame, setCurrentGame] = useState<Game | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const makeApiCall = useApiCall();

  useEffect(() => {
    if (!isOpen || games.length) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    makeApiCall(gameService.getGames)
      .then((resp) => {
        setGames(resp);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });

    return () => {
      if (!user?.id) {
        CachingEngine.clearCache();
      }
    };
  }, [user?.id, isOpen, games.length, makeApiCall]);

  const gameTriggers = useMemo(
    () => ({
      'Tile Pile': () => {
        const game = games.find((g) => g.name === 'Tile Pile');
        if (game) {
          setCurrentGame(game);
          setIsTilePileOpen(true);
          onClose();
        }
      },
      'Word Worm': () => {
        const game = games.find((g) => g.name === 'Word Worm');
        if (game) {
          setCurrentGame(game);
          setIsWordWormOpen(true);
          onClose();
        }
      },
    }),
    [games, setCurrentGame, setIsTilePileOpen, setIsWordWormOpen, onClose],
  );

  const onHighScore = useCallback(
    (score: number) => {
      console.log('onHighScore called with score:', score);
      console.log('Current game state:', currentGame);
      console.log('Current user:', user);

      if (!currentGame) {
        console.log('Missing currentGame, returning');
        return;
      }

      if (user) {
        // Logged in user - update server
        const optimisticGame = {
          ...currentGame,
          user_high_score: {
            username: user.username,
            score: score,
          },
        };
        setCurrentGame(optimisticGame);

        makeApiCall(gameService.setHighScore, currentGame.id, user.id, score)
          .then((game) => {
            console.log('High score updated, new game data:', game);
            setCurrentGame(game);
          })
          .catch((error) => {
            console.error('Error updating high score:', error);
            setCurrentGame(currentGame);
          });
      } else {
        // Anonymous user - update local cookie
        const localHighScore = getLocalHighScore(currentGame.id);
        console.log('Local high score check:', { score, localHighScore });
        if (score > localHighScore) {
          setLocalHighScore(currentGame.id, score);

          // Update global high score if needed, even for anonymous users
          if (score > (currentGame.global_high_score?.score ?? 0)) {
            makeApiCall(gameService.setGlobalHighScore, currentGame.id, score);
          }

          // Update currentGame with local high score using previous state
          setCurrentGame((prev) =>
            prev
              ? {
                  ...prev,
                  user_high_score: {
                    score: score,
                  },
                  user_rank: null,
                  global_high_score: {
                    ...prev.global_high_score,
                    score: Math.max(prev.global_high_score?.score ?? 0, score),
                  },
                }
              : prev,
          );
        }
      }
    },
    [user, currentGame, makeApiCall],
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="user-alert-popup">
          <div className="row game-menu-content">
            <div className="game-menu-image">
              <img src={IMAGES.characters.wormy_arcade} alt="Orator" />
            </div>
            <div className="col">
              <h2>Orator Games</h2>
              {!isLoading &&
                games
                  .filter((g) => g.name in gameTriggers)
                  .map((g) => (
                    <ItemCard
                      key={g.id}
                      label={g.name}
                      color="var(--dark-green)"
                      onClick={gameTriggers[g.name as GameName]}
                    />
                  ))}
            </div>
          </div>
        </div>
      </Modal>
      {isWordWormOpen && currentGame && (
        <WordWormGameModal
          onHighScore={onHighScore}
          gameScore={currentGame}
          onClose={() => {
            reopen();
            setIsWordWormOpen(false);
          }}
          gameId={currentGame.id}
        />
      )}
      {isTilePileOpen && currentGame && (
        <TilePileGameModal
          onHighScore={onHighScore}
          gameScore={currentGame}
          onClose={() => {
            setIsTilePileOpen(false);
            reopen();
          }}
          gameId={currentGame.id}
        />
      )}
    </>
  );
};

export default GameMenu;
